import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  Stack,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  defaultDropAnimationSideEffects,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";

// 项目自评常见一级指标颜色配置（借用事中监控的配色）
const getLevel1Colors = (theme: any) => {
  const isDark = theme.palette.mode === "dark";
  return {
    成本指标: {
      bg: isDark ? "#1a237e" : "#e8eaf6",
      border: isDark ? "#3f51b5" : "#9fa8da",
      text: isDark ? "#c5cae9" : "#1a237e",
    },
    产出指标: {
      bg: isDark ? "#1b5e20" : "#e8f5e9",
      border: isDark ? "#4caf50" : "#a5d6a7",
      text: isDark ? "#c8e6c9" : "#1b5e20",
    },
    效益指标: {
      bg: isDark ? "#4a148c" : "#f3e5f5",
      border: isDark ? "#9c27b0" : "#ce93d8",
      text: isDark ? "#e1bee7" : "#4a148c",
    },
    满意度指标: {
      bg: isDark ? "#e65100" : "#fff3e0",
      border: isDark ? "#ff9800" : "#ffb74d",
      text: isDark ? "#ffe0b2" : "#e65100",
    },
  };
};

// 一级指标编码映射（与后端编码推断规则对应）
const level1CodeMap: Record<string, string> = {
  成本指标: "A",
  产出指标: "B",
  效益指标: "C",
  满意度指标: "D",
};

// 根据拖拽后的层级结构生成新的三级编码
const generateNewCodes = (sortedStructure: any[]) => {
  const newCodes: any[] = [];

  sortedStructure.forEach((level1Group) => {
    const level1Code = level1CodeMap[level1Group.name] || "Z";

    level1Group.level2Groups.forEach((level2Group: any, level2Index: number) => {
      const level2Code = `${level1Code}${level2Index + 1}`;

      level2Group.indicators.forEach((indicator: any, level3Index: number) => {
        const level3Code = `${level2Code}${String(level3Index + 1).padStart(2, "0")}`;
        newCodes.push({
          id: indicator.id,
          level3_code: level3Code,
        });
      });
    });
  });

  return newCodes;
};

// 从当前指标列表构建分组后的排序结构
const buildInitialStructure = (indicators: any[]) => {
  const level1Groups: any = {};
  indicators.forEach((indicator) => {
    if (!level1Groups[indicator.level1_name]) {
      level1Groups[indicator.level1_name] = {};
    }
    if (!level1Groups[indicator.level1_name][indicator.level2_name]) {
      level1Groups[indicator.level1_name][indicator.level2_name] = [];
    }
    level1Groups[indicator.level1_name][indicator.level2_name].push(indicator);
  });

  const structure = Object.entries(level1Groups).map(
    ([level1Name, level2Groups]: [string, any]) => ({
      id: `level1-${level1Name}`,
      name: level1Name,
      level2Groups: Object.entries(level2Groups)
        .map(([level2Name, indicators]: [string, any]) => ({
          id: `level2-${level1Name}-${level2Name}`,
          name: level2Name,
          indicators: indicators.sort((a: any, b: any) => {
            const aCode = a.level3_code || "";
            const bCode = b.level3_code || "";
            return aCode.localeCompare(bCode);
          }),
        }))
        .sort((a: any, b: any) => {
          const aCode = a.indicators[0]?.level3_code || "";
          const bCode = b.indicators[0]?.level3_code || "";
          return aCode.localeCompare(bCode);
        }),
    }),
  );

  const nameOrder: Record<string, number> = {
    成本指标: 0,
    产出指标: 1,
    效益指标: 2,
    满意度指标: 3,
  };
  const codeOrder: Record<string, number> = {
    A: 0,
    B: 1,
    C: 2,
    D: 3,
  };

  return structure.sort((a: any, b: any) => {
    const aCode = a.level2Groups[0]?.indicators[0]?.level1_code || "";
    const bCode = b.level2Groups[0]?.indicators[0]?.level1_code || "";
    const aOrder = codeOrder[aCode] ?? nameOrder[a.name] ?? 99;
    const bOrder = codeOrder[bCode] ?? nameOrder[b.name] ?? 99;
    return aOrder - bOrder;
  });
};

const findActiveItem = (id: number, structure: any[]) => {
  for (const level1Group of structure) {
    for (const level2Group of level1Group.level2Groups) {
      for (const indicator of level2Group.indicators) {
        if (indicator.id === id) {
          return indicator;
        }
      }
    }
  }
  return null;
};

const findDragPositions = (structure: any[], activeId: number, overId: number) => {
  let sourceGroup: any = null;
  let sourceIndex = -1;
  let targetGroup: any = null;
  let targetIndex = -1;

  for (const level1Group of structure) {
    for (const level2Group of level1Group.level2Groups) {
      const activeIndex = level2Group.indicators.findIndex(
        (ind: any) => ind.id === activeId,
      );
      const overIndex = level2Group.indicators.findIndex(
        (ind: any) => ind.id === overId,
      );

      if (activeIndex !== -1) {
        sourceGroup = level2Group;
        sourceIndex = activeIndex;
      }
      if (overIndex !== -1) {
        targetGroup = level2Group;
        targetIndex = overIndex;
      }
    }
  }

  return { sourceGroup, sourceIndex, targetGroup, targetIndex };
};

function SortableIndicator({ indicator }: any) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: indicator.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      sx={{
        p: 1,
        mb: 1,
        display: "flex",
        alignItems: "center",
        cursor: "grab",
        "&:active": {
          cursor: "grabbing",
        },
      }}
      {...attributes}
      {...listeners}
    >
      <DragIndicatorIcon sx={{ mr: 1, color: "text.secondary" }} />
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="body2" fontWeight="bold">
          {indicator.level3_code}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {indicator.level3_name}
        </Typography>
      </Box>
    </Paper>
  );
}

interface ProjectSelfEvaluationIndicatorSortDialogProps {
  open: boolean;
  onClose: () => void;
  indicators: any[];
  onSave: (newCodes: any[]) => Promise<void>;
  isSaving?: boolean;
}

const ProjectSelfEvaluationIndicatorSortDialog: React.FC<
  ProjectSelfEvaluationIndicatorSortDialogProps
> = ({ open, onClose, indicators, onSave, isSaving = false }) => {
  const theme = useTheme();
  const level1Colors = getLevel1Colors(theme);
  const [sortedStructure, setSortedStructure] = useState<any[]>([]);
  const [activeItem, setActiveItem] = useState<any>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    if (indicators && indicators.length > 0) {
      const structure = buildInitialStructure(indicators);
      setSortedStructure(structure);
    }
  }, [indicators]);

  const handleDragStart = (event: any) => {
    const { active } = event;
    const item = findActiveItem(active.id, sortedStructure);
    setActiveItem(item);
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      setActiveItem(null);
      return;
    }

    setSortedStructure((items) => {
      const newStructure = [...items];
      const { sourceGroup, sourceIndex, targetGroup, targetIndex } =
        findDragPositions(newStructure, active.id, over.id);

      if (sourceGroup && targetGroup) {
        const movedItem = sourceGroup.indicators.splice(sourceIndex, 1)[0];
        targetGroup.indicators.splice(targetIndex, 0, movedItem);
      }

      return newStructure;
    });

    setActiveItem(null);
  };

  const handleSave = async () => {
    const newCodes = generateNewCodes(sortedStructure);
    await onSave(newCodes);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">项目自评指标排序</Typography>
          <Button startIcon={<CloseIcon />} onClick={onClose} disabled={isSaving}>
            关闭
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          拖拽指标卡片可以调整指标顺序。排序完成后将自动生成新的指标编码，用于导出 Excel / Word
          时保持顺序一致。
        </Alert>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <Stack spacing={3}>
            {sortedStructure.map((level1Group) => (
              <Paper
                key={level1Group.id}
                elevation={1}
                sx={{
                  p: 2,
                  backgroundColor:
                    level1Colors[level1Group.name as keyof typeof level1Colors]?.bg,
                  border: `1px solid ${
                    level1Colors[level1Group.name as keyof typeof level1Colors]
                      ?.border
                  }`,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color:
                      level1Colors[level1Group.name as keyof typeof level1Colors]
                        ?.text,
                    mb: 2,
                    fontWeight: "bold",
                  }}
                >
                  {level1Group.name}
                </Typography>

                <Stack spacing={2}>
                  {level1Group.level2Groups.map((level2Group: any) => (
                    <Box key={level2Group.id}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color:
                            level1Colors[level1Group.name as keyof typeof level1Colors]
                              ?.text,
                          mb: 1,
                          fontWeight: "medium",
                        }}
                      >
                        {level2Group.name}
                      </Typography>

                      <SortableContext
                        items={level2Group.indicators.map((ind: any) => ind.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <Box sx={{ pl: 2 }}>
                          {level2Group.indicators.map((indicator: any) => (
                            <SortableIndicator key={indicator.id} indicator={indicator} />
                          ))}
                        </Box>
                      </SortableContext>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            ))}
          </Stack>

          <DragOverlay
            dropAnimation={{
              sideEffects: defaultDropAnimationSideEffects({
                styles: {
                  active: {
                    opacity: "0.5",
                  },
                },
              }),
            }}
          >
            {activeItem ? (
              <Paper
                sx={{
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  opacity: 0.8,
                  transform: "rotate(5deg)",
                }}
              >
                <DragIndicatorIcon sx={{ mr: 1, color: "text.secondary" }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" fontWeight="bold">
                    {activeItem.level3_code}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {activeItem.level3_name}
                  </Typography>
                </Box>
              </Paper>
            ) : null}
          </DragOverlay>
        </DndContext>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          取消
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={isSaving}
        >
          {isSaving ? "保存中..." : "保存排序"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectSelfEvaluationIndicatorSortDialog;

